import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Settings, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { ConfirmationDialog } from '../ui/confirmation-dialog';
import OptionsService from '../../Entities/OptionsService';
import { useToast } from '../ui/use-toast';

/**
 * Componente de Gerenciamento de Opções Dinâmicas
 * Acesso exclusivo para SuperAdmin
 */
const OptionsManagement = () => {
  const { toast } = useToast();
  const [tiposParceria, setTiposParceria] = useState([]);
  const [origens, setOrigens] = useState([]);
  const [prazos, setPrazos] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newItemType, setNewItemType] = useState(null);
  const [newItemValue, setNewItemValue] = useState('');
  
  // Estado para diálogo de confirmação
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: null
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadAllOptions();
  }, []);

  const loadAllOptions = async () => {
    try {
      setLoading(true);
      const [tipos, origs, prazs] = await Promise.all([
        OptionsService.getTiposParceria(),
        OptionsService.getOrigens(),
        OptionsService.getPrazos()
      ]);
      setTiposParceria(tipos);
      setOrigens(origs);
      setPrazos(prazs);
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Erro ao carregar opções"
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== TIPO PARCERIA ====================

  const handleCreateTipoParceria = async () => {
    if (!newItemValue.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome não pode ser vazio"
      });
      return;
    }
    try {
      const result = await OptionsService.createTipoParceria(newItemValue.trim());
      toast({
        title: (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span>Tipo de Parceria Criado</span>
          </div>
        ),
        description: `"${result.nome}" foi adicionado com sucesso!`,
        className: "border-green-200 bg-green-50"
      });
      setNewItemValue('');
      setNewItemType(null);
      loadAllOptions();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.error || 'Erro ao criar tipo de parceria'
      });
    }
  };

  const handleUpdateTipoParceria = async (id) => {
    if (!editValue.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome não pode ser vazio"
      });
      return;
    }
    try {
      const result = await OptionsService.updateTipoParceria(id, editValue.trim());
      toast({
        title: (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />
            <span>Atualizado</span>
          </div>
        ),
        description: `Tipo de parceria atualizado para "${result.nome}"`,
        className: "border-blue-200 bg-blue-50"
      });
      setEditingItem(null);
      setEditValue('');
      loadAllOptions();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.error || 'Erro ao atualizar tipo de parceria'
      });
    }
  };

  const handleDeleteTipoParceria = async (id) => {
    const tipo = tiposParceria.find(t => t.id === id);
    
    setConfirmDialog({
      isOpen: true,
      title: 'Deletar Tipo de Parceria',
      description: `Tem certeza que deseja deletar "${tipo?.nome}"? Esta ação pode ser revertida posteriormente.`,
      onConfirm: async () => {
        try {
          await OptionsService.deleteTipoParceria(id);
          toast({
            title: (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-orange-600" />
                <span>Deletado</span>
              </div>
            ),
            description: `"${tipo?.nome}" foi removido com sucesso`,
            className: "border-orange-200 bg-orange-50"
          });
          loadAllOptions();
        } catch (error) {
          toast({
            variant: "destructive",
            title: (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Erro ao Deletar</span>
              </div>
            ),
            description: error.response?.data?.error || 'Erro ao deletar tipo de parceria'
          });
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  // ==================== ORIGEM ====================

  const handleCreateOrigem = async () => {
    if (!newItemValue.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome não pode ser vazio"
      });
      return;
    }
    try {
      const result = await OptionsService.createOrigem(newItemValue.trim());
      toast({
        title: (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span>Origem Criada</span>
          </div>
        ),
        description: `"${result.nome}" foi adicionada com sucesso!`,
        className: "border-green-200 bg-green-50"
      });
      setNewItemValue('');
      setNewItemType(null);
      loadAllOptions();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.error || 'Erro ao criar origem'
      });
    }
  };

  const handleUpdateOrigem = async (id) => {
    if (!editValue.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome não pode ser vazio"
      });
      return;
    }
    try {
      const result = await OptionsService.updateOrigem(id, editValue.trim());
      toast({
        title: (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />
            <span>Atualizado</span>
          </div>
        ),
        description: `Origem atualizada para "${result.nome}"`,
        className: "border-blue-200 bg-blue-50"
      });
      setEditingItem(null);
      setEditValue('');
      loadAllOptions();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.error || 'Erro ao atualizar origem'
      });
    }
  };

  const handleDeleteOrigem = async (id) => {
    const origem = origens.find(o => o.id === id);
    
    setConfirmDialog({
      isOpen: true,
      title: 'Deletar Origem',
      description: `Tem certeza que deseja deletar "${origem?.nome}"? Esta ação pode ser revertida posteriormente.`,
      onConfirm: async () => {
        try {
          await OptionsService.deleteOrigem(id);
          toast({
            title: (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-orange-600" />
                <span>Deletado</span>
              </div>
            ),
            description: `"${origem?.nome}" foi removida com sucesso`,
            className: "border-orange-200 bg-orange-50"
          });
          loadAllOptions();
        } catch (error) {
          toast({
            variant: "destructive",
            title: (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Erro ao Deletar</span>
              </div>
            ),
            description: error.response?.data?.error || 'Erro ao deletar origem'
          });
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  // ==================== PRAZOS ====================

  const handleCreatePrazo = async () => {
    const dias = parseInt(newItemValue);
    if (isNaN(dias) || dias <= 0) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Dias deve ser um número positivo"
      });
      return;
    }
    try {
      const result = await OptionsService.createPrazo(dias);
      toast({
        title: (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span>Prazo Criado</span>
          </div>
        ),
        description: `Prazo de ${result.dias} dias foi adicionado com sucesso!`,
        className: "border-green-200 bg-green-50"
      });
      setNewItemValue('');
      setNewItemType(null);
      loadAllOptions();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.error || 'Erro ao criar prazo'
      });
    }
  };

  const handleUpdatePrazo = async (id) => {
    const dias = parseInt(editValue);
    if (isNaN(dias) || dias <= 0) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Dias deve ser um número positivo"
      });
      return;
    }
    try {
      const result = await OptionsService.updatePrazo(id, dias);
      toast({
        title: (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />
            <span>Atualizado</span>
          </div>
        ),
        description: `Prazo atualizado para ${result.dias} dias`,
        className: "border-blue-200 bg-blue-50"
      });
      setEditingItem(null);
      setEditValue('');
      loadAllOptions();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.error || 'Erro ao atualizar prazo'
      });
    }
  };

  const handleDeletePrazo = async (id) => {
    const prazo = prazos.find(p => p.id === id);
    
    setConfirmDialog({
      isOpen: true,
      title: 'Deletar Prazo',
      description: `Tem certeza que deseja deletar o prazo de ${prazo?.dias} dias? Esta ação pode ser revertida posteriormente.`,
      onConfirm: async () => {
        try {
          await OptionsService.deletePrazo(id);
          toast({
            title: (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-orange-600" />
                <span>Deletado</span>
              </div>
            ),
            description: `Prazo de ${prazo?.dias} dias foi removido com sucesso`,
            className: "border-orange-200 bg-orange-50"
          });
          loadAllOptions();
        } catch (error) {
          toast({
            variant: "destructive",
            title: (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Erro ao Deletar</span>
              </div>
            ),
            description: error.response?.data?.error || 'Erro ao deletar prazo'
          });
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  // ==================== COMPONENTES DE RENDERIZAÇÃO ====================

  const renderOptionsList = (items, type) => {
    const isEditing = (item) => editingItem?.id === item.id && editingItem?.type === type;
    
    const getHandlers = () => {
      switch(type) {
        case 'tipo':
          return { update: handleUpdateTipoParceria, delete: handleDeleteTipoParceria };
        case 'origem':
          return { update: handleUpdateOrigem, delete: handleDeleteOrigem };
        case 'prazo':
          return { update: handleUpdatePrazo, delete: handleDeletePrazo };
        default:
          return {};
      }
    };

    const handlers = getHandlers();

    return (
      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="group relative"
          >
            {isEditing(item) ? (
              <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300 shadow-sm">
                <Input
                  type={type === 'prazo' ? 'number' : 'text'}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 border-blue-200 focus:border-blue-400"
                  placeholder={type === 'prazo' ? 'Dias' : 'Nome'}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handlers.update(item.id);
                  }}
                />
                <Button
                  size="sm"
                  onClick={() => handlers.update(item.id)}
                  className="bg-green-600 hover:bg-green-700 shadow-md"
                >
                  <Save className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingItem(null);
                    setEditValue('');
                  }}
                  className="hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 group-hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 text-sm font-semibold">
                    {index + 1}
                  </div>
                  <span className="text-slate-800 font-medium">
                    {type === 'prazo' ? `${item.dias} dias` : item.nome}
                  </span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingItem({ id: item.id, type });
                      setEditValue(type === 'prazo' ? item.dias.toString() : item.nome);
                    }}
                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handlers.delete(item.id)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Deletar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderNewItemForm = (type, placeholder, onSubmit) => {
    if (newItemType !== type) {
      return (
        <Button
          onClick={() => setNewItemType(type)}
          variant="outline"
          className="w-full border-dashed border-2 border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 py-6"
        >
          <Plus className="w-5 h-5 mr-2" />
          <span className="font-medium">Adicionar Novo</span>
        </Button>
      );
    }

    return (
      <div className="flex items-center gap-2 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300 shadow-sm">
        <Input
          type={type === 'prazo' ? 'number' : 'text'}
          value={newItemValue}
          onChange={(e) => setNewItemValue(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border-blue-200 focus:border-blue-400 bg-white"
          autoFocus
          onKeyPress={(e) => {
            if (e.key === 'Enter') onSubmit();
          }}
        />
        <Button
          size="sm"
          onClick={onSubmit}
          className="bg-green-600 hover:bg-green-700 shadow-md px-4"
        >
          <Save className="w-4 h-4 mr-1" />
          Salvar
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setNewItemType(null);
            setNewItemValue('');
          }}
          className="hover:bg-red-50 hover:text-red-600 hover:border-red-300"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-lg font-medium text-slate-700">Carregando opções...</p>
          <p className="text-sm text-slate-500 mt-1">Aguarde um momento</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header Moderno */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-2xl p-8 shadow-xl">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
            <Settings className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Opções Dinâmicas</h1>
            <p className="text-blue-100">Gerencie os valores disponíveis para seleção nos formulários de parcerias</p>
          </div>
          <div className="hidden lg:flex items-center gap-6 text-white/90">
            <div className="text-center">
              <div className="text-2xl font-bold">{tiposParceria.length}</div>
              <div className="text-xs uppercase tracking-wide">Tipos</div>
            </div>
            <div className="w-px h-12 bg-white/30"></div>
            <div className="text-center">
              <div className="text-2xl font-bold">{origens.length}</div>
              <div className="text-xs uppercase tracking-wide">Origens</div>
            </div>
            <div className="w-px h-12 bg-white/30"></div>
            <div className="text-center">
              <div className="text-2xl font-bold">{prazos.length}</div>
              <div className="text-xs uppercase tracking-wide">Prazos</div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tipos de Parceria */}
        <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="text-xl font-bold mb-1">Tipos de Parceria</h3>
                <p className="text-blue-100 text-sm">{tiposParceria.length} tipo(s) cadastrado(s)</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="p-6">
            {tiposParceria.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nenhum tipo cadastrado</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {renderOptionsList(tiposParceria, 'tipo')}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-200">
              {renderNewItemForm('tipo', 'Nome do tipo de parceria', handleCreateTipoParceria)}
            </div>
          </div>
        </Card>

        {/* Origens */}
        <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6">
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="text-xl font-bold mb-1">Origens</h3>
                <p className="text-green-100 text-sm">{origens.length} origem(ns) cadastrada(s)</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <Settings className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="p-6">
            {origens.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nenhuma origem cadastrada</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {renderOptionsList(origens, 'origem')}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-200">
              {renderNewItemForm('origem', 'Nome da origem', handleCreateOrigem)}
            </div>
          </div>
        </Card>

        {/* Prazos */}
        <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6">
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="text-xl font-bold mb-1">Prazos</h3>
                <p className="text-amber-100 text-sm">{prazos.length} prazo(s) cadastrado(s)</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <Plus className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="p-6">
            {prazos.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Plus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nenhum prazo cadastrado</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {renderOptionsList(prazos, 'prazo')}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-200">
              {renderNewItemForm('prazo', 'Número de dias', handleCreatePrazo)}
            </div>
          </div>
        </Card>
      </div>

      {/* Info Footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Settings className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 mb-1">Como funciona?</h4>
            <p className="text-sm text-blue-700">
              As opções cadastradas aqui estarão disponíveis nos formulários de criação e edição de parcerias. 
              Você pode adicionar, editar ou remover opções a qualquer momento. As alterações são aplicadas imediatamente.
            </p>
          </div>
        </div>
      </div>

      {/* Diálogo de Confirmação */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText="Confirmar"
        cancelText="Cancelar"
        variant="destructive"
      />
    </div>
  );
};

export default OptionsManagement;
